import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AuthUser } from 'aws-amplify/auth';
import { LeaderboardEntry, Squad } from '../types';
import { getLeaderboard, getMySquads, createSquad, joinSquad, getSquadLeaderboard } from '../api';
import './Leaderboard.css';

interface Props {
  displayName: string;
  user: AuthUser | null;
}

type LeaderboardTab = 'global' | 'squad' | 'country';

export default function Leaderboard({ displayName, user }: Props) {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<LeaderboardTab>('global');

  // Squad state
  const [squads, setSquads] = useState<Squad[]>([]);
  const [squadsLoading, setSquadsLoading] = useState(false);
  const [activeSquadId, setActiveSquadId] = useState<string | null>(null);
  const [squadLb, setSquadLb] = useState<LeaderboardEntry[]>([]);
  const [squadLbLoading, setSquadLbLoading] = useState(false);
  const [squadLbLoaded, setSquadLbLoaded] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [createName, setCreateName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [squadError, setSquadError] = useState('');
  const [squadWorking, setSquadWorking] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getLeaderboard().then(realData => {
      console.log('[Leaderboard] raw API response:', realData);
      const realRanked = realData.map((u, i) => ({ ...u, rank: i + 1 }));
      setEntries(realRanked);
      setLoading(false);
    }).catch(err => {
      console.error('[Leaderboard] getLeaderboard() failed:', err);
      setLoading(false);
    });
  }, []);

  const loadSquadLb = useCallback(async (squadId: string) => {
    setSquadLbLoading(true);
    setSquadLbLoaded(false);
    const data = await getSquadLeaderboard(squadId);
    setSquadLb(data);
    setSquadLbLoading(false);
    setSquadLbLoaded(true);
  }, []);

  useEffect(() => {
    if (activeTab !== 'squad' || !user) return;
    setSquadsLoading(true);
    getMySquads().then(data => {
      setSquads(data);
      setSquadsLoading(false);
      if (data.length > 0) {
        const first = data[0].squadId;
        setActiveSquadId(first);
        loadSquadLb(first);
      }
    });
  }, [activeTab, user, loadSquadLb]);

  useEffect(() => {
    if (activeSquadId) loadSquadLb(activeSquadId);
  }, [activeSquadId, loadSquadLb]);

  const handleCreateSquad = async () => {
    if (!createName.trim()) return;
    setSquadWorking(true);
    setSquadError('');
    try {
      const squad = await createSquad(createName.trim(), displayName);
      if (squad) {
        const updated = await getMySquads();
        setSquads(updated);
        setActiveSquadId(squad.squadId);
        setShowCreate(false);
        setCreateName('');
      }
    } catch (e: any) {
      setSquadError(e.message ?? 'Failed to create squad');
    } finally {
      setSquadWorking(false);
    }
  };

  const handleJoinSquad = async () => {
    if (!joinCode.trim()) return;
    setSquadWorking(true);
    setSquadError('');
    try {
      const squad = await joinSquad(joinCode.trim(), displayName);
      if (squad) {
        const updated = await getMySquads();
        setSquads(updated);
        setActiveSquadId(squad.squadId);
        setShowJoin(false);
        setJoinCode('');
      }
    } catch (e: any) {
      setSquadError(e.message ?? 'Invalid invite code');
    } finally {
      setSquadWorking(false);
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const yourEntry = user ? entries.find(e => e.userId === user.userId) : null;
  const yourRank = yourEntry ? yourEntry.rank : entries.length + 1;

  if (loading) {
    return (
      <div className="leaderboard-page container">
        <div className="loading-state"><div className="spinner" /><p>Loading leaderboard…</p></div>
      </div>
    );
  }

  return (
    <div className="leaderboard-page container">
      <div className="matches-header">
        <h1 className="page-title">Leaderboard</h1>
        <p className="page-sub">Top predictors — ranked by independent thinking</p>
      </div>

      {/* Tab bar */}
      <div className="lb-tabs">
        <button
          className={`lb-tab${activeTab === 'global' ? ' active' : ''}`}
          onClick={() => setActiveTab('global')}
        >
          🌍 Global
        </button>
        <button
          className={`lb-tab${activeTab === 'squad' ? ' active' : ''}`}
          onClick={() => setActiveTab('squad')}
        >
          👥 My Squad
        </button>
        <button
          className={`lb-tab${activeTab === 'country' ? ' active' : ''}`}
          onClick={() => setActiveTab('country')}
        >
          🏳️ By Country
        </button>
      </div>

      {/* Non-global tabs — coming soon */}
      {activeTab === 'squad' && !user && (
        <div className="lb-coming-soon">
          <div className="lb-cs-icon">👥</div>
          <h3>Sign in to use squads</h3>
          <p>Create a private squad, share your invite code, and compete with friends on your own leaderboard.</p>
          <Link to="/login" className="lb-blur-notice-btn">Sign In Free</Link>
        </div>
      )}

      {activeTab === 'squad' && user && squadsLoading && (
        <div className="loading-state"><div className="spinner" /><p>Loading squads…</p></div>
      )}

      {activeTab === 'squad' && user && !squadsLoading && squads.length === 0 && (
        <div className="squad-empty">
          <div className="squad-empty-icon">👥</div>
          <h3>No squads yet</h3>
          <p>Create your own squad or join a friend's with an invite code.</p>
          <div className="squad-cta-row">
            <button className="squad-cta-btn squad-cta-primary" onClick={() => { setShowCreate(true); setShowJoin(false); setSquadError(''); }}>
              + Create a Squad
            </button>
            <button className="squad-cta-btn squad-cta-secondary" onClick={() => { setShowJoin(true); setShowCreate(false); setSquadError(''); }}>
              Enter Invite Code
            </button>
          </div>
          {showCreate && (
            <div className="squad-form">
              <p className="squad-form-label">Squad name</p>
              <input
                className="squad-input"
                placeholder="e.g. The Pundits"
                value={createName}
                maxLength={30}
                onChange={e => setCreateName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateSquad()}
              />
              {squadError && <p className="squad-error">{squadError}</p>}
              <button className="squad-form-btn" onClick={handleCreateSquad} disabled={squadWorking || !createName.trim()}>
                {squadWorking ? 'Creating…' : 'Create Squad'}
              </button>
            </div>
          )}
          {showJoin && (
            <div className="squad-form">
              <p className="squad-form-label">Invite code</p>
              <input
                className="squad-input squad-input-code"
                placeholder="e.g. ABCD23"
                value={joinCode}
                maxLength={8}
                onChange={e => setJoinCode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && handleJoinSquad()}
              />
              {squadError && <p className="squad-error">{squadError}</p>}
              <button className="squad-form-btn" onClick={handleJoinSquad} disabled={squadWorking || !joinCode.trim()}>
                {squadWorking ? 'Joining…' : 'Join Squad'}
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'squad' && user && !squadsLoading && squads.length > 0 && (() => {
        const activeSquad = squads.find(s => s.squadId === activeSquadId) ?? squads[0];
        return (
          <>
            {/* Squad selector */}
            {squads.length > 1 && (
              <div className="squad-selector">
                {squads.map(s => (
                  <button
                    key={s.squadId}
                    className={`squad-selector-btn${s.squadId === activeSquad.squadId ? ' active' : ''}`}
                    onClick={() => setActiveSquadId(s.squadId)}
                  >
                    {s.name}
                    <span className="squad-member-count">{s.memberCount} members</span>
                  </button>
                ))}
              </div>
            )}

            {/* Squad header */}
            <div className="squad-header-bar">
              <div className="squad-header-info">
                <span className="squad-header-name">{activeSquad.name}</span>
                <span className="squad-header-count">{activeSquad.memberCount} member{activeSquad.memberCount !== 1 ? 's' : ''}</span>
              </div>
              <div className="squad-invite-row">
                <span className="squad-invite-label">Invite code</span>
                <span className="squad-invite-code">{activeSquad.inviteCode}</span>
                <button className="squad-copy-btn" onClick={() => handleCopy(activeSquad.inviteCode)}>
                  {copied ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Squad leaderboard */}
            {squadLbLoading && <div className="loading-state"><div className="spinner" /><p>Loading squad leaderboard…</p></div>}

            {!squadLbLoading && squadLbLoaded && (
              <div className="lb-table">
                <div className="lb-row lb-head">
                  <span className="lb-rank">#</span>
                  <span className="lb-user">Player</span>
                  <span className="lb-flag-col">🌍</span>
                  <span className="lb-pts">Points</span>
                  <span className="lb-streak">Streak</span>
                  <span className="lb-bust">AI BUST%</span>
                  <span className="lb-badges">Badges</span>
                </div>
                {squadLb.length === 0 && (
                  <div className="lb-empty">No predictions scored yet — check back after matches</div>
                )}
                {squadLb.map(entry => (
                  <div
                    key={entry.userId}
                    className={[
                      'lb-row',
                      entry.rank <= 3 ? `top-${entry.rank}` : '',
                      entry.userId === user.userId ? 'lb-row-you' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <span className="lb-rank">
                      {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank}
                    </span>
                    <span className="lb-user">
                      <span className={`lb-avatar${entry.userId === user.userId ? ' lb-avatar-you' : ''}`}>
                        {entry.username.slice(0, 2).toUpperCase()}
                      </span>
                      {entry.username}
                      {entry.userId === user.userId && <span className="lb-you-badge">YOU</span>}
                    </span>
                    <span className="lb-flag-col">{entry.countryFlag ?? ''}</span>
                    <span className="lb-pts">{entry.points}</span>
                    <span className={`lb-streak${!(entry.streak > 0) ? ' lb-muted' : ''}`}>
                      {entry.streak > 0 ? `🔥 ${entry.streak}` : '—'}
                    </span>
                    <span className={`lb-bust${entry.aiBustRate == null ? ' lb-bust-empty' : ''}`}>
                      {entry.aiBustRate != null ? `${entry.aiBustRate}%` : '—'}
                    </span>
                    <span className="lb-badges">
                      {entry.badges && entry.badges.length > 0 ? entry.badges.join('') : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Bottom actions */}
            <div className="squad-actions">
              <button className="squad-cta-btn squad-cta-secondary" onClick={() => { setShowJoin(v => !v); setShowCreate(false); setSquadError(''); }}>
                + Join another squad
              </button>
              <button className="squad-cta-btn squad-cta-secondary" onClick={() => { setShowCreate(v => !v); setShowJoin(false); setSquadError(''); }}>
                + Create new squad
              </button>
            </div>

            {showJoin && (
              <div className="squad-form">
                <p className="squad-form-label">Invite code</p>
                <input
                  className="squad-input squad-input-code"
                  placeholder="e.g. ABCD23"
                  value={joinCode}
                  maxLength={8}
                  onChange={e => setJoinCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleJoinSquad()}
                />
                {squadError && <p className="squad-error">{squadError}</p>}
                <button className="squad-form-btn" onClick={handleJoinSquad} disabled={squadWorking || !joinCode.trim()}>
                  {squadWorking ? 'Joining…' : 'Join Squad'}
                </button>
              </div>
            )}
            {showCreate && (
              <div className="squad-form">
                <p className="squad-form-label">Squad name</p>
                <input
                  className="squad-input"
                  placeholder="e.g. The Pundits"
                  value={createName}
                  maxLength={30}
                  onChange={e => setCreateName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreateSquad()}
                />
                {squadError && <p className="squad-error">{squadError}</p>}
                <button className="squad-form-btn" onClick={handleCreateSquad} disabled={squadWorking || !createName.trim()}>
                  {squadWorking ? 'Creating…' : 'Create Squad'}
                </button>
              </div>
            )}
          </>
        );
      })()}

      {activeTab === 'country' && (
        <div className="lb-coming-soon">
          <div className="lb-cs-icon">🏳️</div>
          <h3>By Country — Coming Soon</h3>
          <p>Country-specific rankings are coming in the next update. Set your country flag on your profile to be ready.</p>
          {!user && <Link to="/login" className="lb-blur-notice-btn">Sign In to Get Early Access</Link>}
        </div>
      )}

      {/* Global leaderboard */}
      {activeTab === 'global' && (
        <>
          <div className="lb-table">
            <div className="lb-row lb-head">
              <span className="lb-rank">#</span>
              <span className="lb-user">Player</span>
              <span className="lb-flag-col">🌍</span>
              <span className="lb-pts">Points</span>
              <span className="lb-streak">Streak 🔥</span>
              <span className="lb-bust">AI BUST%</span>
              <span className="lb-badges">Badges</span>
            </div>

            {entries.length === 0 && (
              <div className="lb-empty">No predictions scored yet — check back after today's matches</div>
            )}

            {entries.map((entry) => (
              <div
                key={entry.rank}
                className={[
                  'lb-row',
                  entry.rank <= 3 ? `top-${entry.rank}` : '',
                  entry.rank > 10 && !user ? 'lb-row-blurred' : '',
                  entry.userId === user?.userId ? 'lb-row-you' : '',
                ].filter(Boolean).join(' ')}
                aria-hidden={entry.rank > 10 && !user ? 'true' : undefined}
              >
                <span className="lb-rank">
                  {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank}
                </span>
                <span className="lb-user">
                  <span className="lb-avatar">{entry.username.slice(0, 2).toUpperCase()}</span>
                  {entry.username}
                  {entry.userId === user?.userId && <span className="lb-you-badge">YOU</span>}
                </span>
                <span className="lb-flag-col">{entry.countryFlag ?? ''}</span>
                <span className="lb-pts">{entry.points}</span>
                <span className={`lb-streak${!(entry.streak > 0) ? ' lb-muted' : ''}`}>
                  {entry.streak > 0 ? `🔥 ${entry.streak}` : '—'}
                </span>
                <span className={`lb-bust${entry.aiBustRate == null ? ' lb-bust-empty' : ''}`}>
                  {entry.aiBustRate != null ? `${Math.round(entry.aiBustRate * 100)}%` : '—'}
                </span>
                <span className="lb-badges">
                  {entry.badges && entry.badges.length > 0 ? entry.badges.join('') : '—'}
                </span>
              </div>
            ))}

            {/* YOU row — only if logged in and not already in entries */}
            {user && !yourEntry && (
              <div className="lb-row lb-row-you">
                <span className="lb-rank">{yourRank}</span>
                <span className="lb-user">
                  <span className="lb-avatar lb-avatar-you">{displayName.slice(0, 2).toUpperCase()}</span>
                  {displayName}
                  <span className="lb-you-badge">YOU</span>
                </span>
                <span className="lb-flag-col" />
                <span className="lb-pts">0</span>
                <span className="lb-streak">—</span>
                <span className="lb-bust">—</span>
                <span className="lb-badges">—</span>
              </div>
            )}

            {/* Guest sign-in prompt */}
            {!user && (
              <div className="lb-row lb-row-guest">
                <span className="lb-rank">—</span>
                <span className="lb-user lb-guest-cta">
                  <Link to="/login" className="lb-guest-link">Sign in to join the leaderboard →</Link>
                </span>
                <span className="lb-flag-col" />
                <span className="lb-pts">—</span>
                <span className="lb-streak">—</span>
                <span className="lb-bust">—</span>
                <span className="lb-badges">—</span>
              </div>
            )}
          </div>

          {!user && (
            <div className="lb-blur-notice">
              <span>🔒</span>
              <span>Sign in to see all rankings and track your position</span>
              <Link to="/login" className="lb-blur-notice-btn">Sign In Free</Link>
            </div>
          )}

          <div className="lb-badge-legend">
            <p className="lb-badge-legend-title">Badge Guide</p>
            <div className="lb-badge-items">
              <span className="lb-badge-item">🤖 AI Buster — correctly challenged AI 5+ times</span>
              <span className="lb-badge-item">🧠 Purist — 10+ blind correct predictions</span>
              <span className="lb-badge-item">🎯 Exacto — 5+ exact scorelines correct</span>
              <span className="lb-badge-item">🔥 On Fire — 5 correct in a row</span>
              <span className="lb-badge-item">💀 Contrarian — challenged AI 10+ times</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
